import React from 'react';
import '../../App.scss';
import { SidebarProps } from './interfaces/ISidebar';
import G20LogoGradient from '../../assets/xealed_down.png';
import {
    ClientIcon,
    MessageIcon,
    NDAsIcon,
    BuildingIcon,
    BriefcaseIcon,
    FundsIcon,
    SpecialSituationsIcon,
    StarIcon,
    FileIcon,
    PipelineIcon,
    SettingsIcon,
    CodeIcon,
    HomeIcon,
    DashboardIcon,
    AnalysisIcon
} from '../utils/IconSet';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

/**
 * Sidebar
 * Renders the sidebar navigation menu with icons and routes.
 * @component
 * @param {SidebarProps} props - Props for Sidebar.
 * @returns {JSX.Element} Sidebar navigation component.
 */
const Sidebar = ({ onClose, activeRoute, onNavigate, user, appSettings, theme = 'light', isOpen = true }: SidebarProps) => {
    
      
    const menuSections = [
        {
            title: '',
            items: [
                {
                    route: 'home',
                    icon: <HomeIcon className='sidebar-menu-icon' />, 
                    label: 'Home',
                },
                {
                    route: 'dashboard',
                    icon: <DashboardIcon className='sidebar-menu-icon' />, 
                    label: 'Dashboard',
                },
            ],
        },
        {
            title: 'Marketplace',
            items: [
                {
                    route: 'objects',
                    icon: <BuildingIcon className='sidebar-menu-icon' />, 
                    label: 'Real Estate',
                },
                {
                    route: 'acquisitions',
                    icon: <BriefcaseIcon className='sidebar-menu-icon' />, 
                    label: 'Private Equity',
                },
                // ...existing code...
                {
                    route: 'special',
                    icon: <SpecialSituationsIcon className='sidebar-menu-icon' />, 
                    label: 'Special Situations ',
                },
                {
                    route: 'funds',
                    icon: <FundsIcon className='sidebar-menu-icon' />, 
                    label: 'Funds',
                },
            ],
        },
        {
            title: 'Workflow',
            items: [
                {
                    route: 'watchlist',
                    icon: <StarIcon className='sidebar-menu-icon' />, 
                    label: 'Watch List',
                },
                {
                    route: 'ndas',
                    icon: <NDAsIcon className='sidebar-menu-icon' />, 
                    label: 'NDAs',
                },
                {
                    route: 'requests',
                    icon: <PipelineIcon className='sidebar-menu-icon' />, 
                    label: 'Requests',
                },
                {
                    route: 'messages',
                    icon: <MessageIcon className='sidebar-menu-icon' />, 
                    label: 'Messages',
                },
                {
                    route: 'documents',
                    icon: <FileIcon className='sidebar-menu-icony' />, 
                    label: 'Documents',
                },
                {
                    route: 'analytics',
                    icon: <AnalysisIcon className='sidebar-menu-icon' />, 
                    label: 'Analytics',
                },
            ],
        },
        {
            title: 'Account',
            items: [
                {
                    route: 'profile',
                    icon: <ClientIcon className='sidebar-menu-icon' />, 
                    label: 'Profile',
                },
                // Show 'Settings' for admin, 'Requests' for others
                ...(user && user.role_id === 1
                    ? [
                        {
                            route: 'settings',
                            icon: <SettingsIcon className='sidebar-menu-icon' />, 
                            label: 'Settings',
                        },
                        {
                            route: 'crm',
                            icon: <ClientIcon className='sidebar-menu-icon' />,
                            label: 'CRM',
                        },
                    ] : []
                  ),
                {
                    route: 'support',
                    icon: <LocalHospitalIcon className='sidebar-menu-icon' />,
                    label: 'Support',
                },
            ],
        },
        {
            title: 'Developer',
            items: [
                {
                    route: 'api',
                    icon: <CodeIcon className='sidebar-menu-icon' />, 
                    label: 'API',
                    href: (typeof window !== 'undefined' && window.location.hostname.includes('localhost'))
                        ? 'http://localhost:8090/api-docs/'
                        : 'https://xm-backend-api-ebbngahacthfdwat.switzerlandnorth-01.azurewebsites.net/api-docs/',
                    external: true,
                },
            ],
        },
    ];

    const handleItemClick = (route: string, label: string) => {
        onNavigate && onNavigate(route, label);

        // Close sidebar on mobile
        if (window.innerWidth <= 768) {
            onClose && onClose();
        }
    };

    return (
        <>
            {/* Overlay: only visible on mobile when sidebar is open */}
            <div
                className={`sidebar-overlay${isOpen ? ' visible' : ''}`}
                onClick={onClose}
            />

            <div className={`sidebar ${isOpen ? 'open' : ''} ${theme}`}>
                {/*<div className='sidebar-bg-image' style={{ backgroundImage: `url(${G20GradientVideo})` }} />*/}
                <div className='sidebar-content'>
                    {/* Scrollable content */}
                    <div className='sidebar-scrollable'>
                        {/* Header with logo and company info */}
                        <div className='sidebar-header'>
                            <div className='logo-container'>
                                <img
                                    src={G20LogoGradient}
                                    alt='App Logo'
                                    className='app-logo'
                                    onError={(e: any) => {
                                        (e.target as HTMLImageElement).src = '/assets/G-20_Logo_Gradient.png'; // Fallback
                                    }}
                                />
                            </div>
                            <div className='app-info'>
                                <h3 className='app-name'>{appSettings?.appName || 'Trading App'}</h3>
                                <p className='company-name'>{user?.companyName || 'Your Company'}</p>
                            </div>
                        </div>

                        {/* Menu Items */}
                        <nav className="sidebar-nav">
                            <ul className="menu-list">
                                {menuSections.map((section, index) => (
                                    <li className="menu-section" key={section.title || index}>
                                        {section.title && (
                                            <div className="menu-section-title">
                                                {section.title}
                                            </div>
                                        )}

                                        <ul className="menu-list">
                                            {section.items.map((item) => (
                                                <li key={item.label} className="sidebar-menu-item">
                                                    {item.external ? (
                                                        <a
                                                            className="menu-button"
                                                            href={item.href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <span className="sidebar-menu-icon">{item.icon}</span>
                                                            <span className="sidebar-menu-label">{item.label}</span>
                                                        </a>
                                                    ) : (
                                                        <button
                                                            className={`menu-button ${activeRoute === item.route ? 'active' : ''}`}
                                                            onClick={() => handleItemClick(item.route, item.label)}
                                                        >
                                                            <span className="sidebar-menu-icon">{item.icon}</span>
                                                            <span className="sidebar-menu-label">{item.label}</span>
                                                        </button>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                    {/* Fixed version at bottom */}
                    <div className='sidebar-footer'>
                        <img src={G20LogoGradient} alt='G-20 Logo' style={{ height: 32, marginRight: 8 }} />{' '}
                        <p className='version-text' style={{ margin: 0 }}>
                            v.3.4.2
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
// --- end Sidebar ---
